// @flow
import {List, fromJS} from 'immutable';
import {Store} from 'client/framework';
import constants from 'client/constants';
import schoolAdministrationActions from './SchoolAdministrationActions';
import PaginationStore from 'generic/PaginationV2/Pagination.store';
import {resource, type QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {SchoolModelV4} from 'resources/GeneratedModels/School/SchoolModel.v4';

const STORE_NAME = 'SchoolAdministrationStore';

export class SchoolAdministrationStore extends Store {
  constructor(name) {
    super(name);

    this.defaultData = fromJS({
      activeUserId: null,
      activeSchoolId: null,
      menuItems: null
    });

    this.setInitialData(this.defaultData);
    this._paginationStore = new PaginationStore(`${STORE_NAME}PaginationStore`);
    this._pageSize = 21;

    this.handle(schoolAdministrationActions.INITIALIZE_MENU_ITEMS, this._initializeMenuItems);
    this.handle(schoolAdministrationActions.SET_ACTIVE_SCHOOL_ID, this._setActiveSchoolId);
    this.handle(schoolAdministrationActions.SET_ACTIVE_USER_ID, this._setActiveUserId);
  }

  _setActiveSchoolId(id: string) {
    this.writeData('activeSchoolId', id);
  }

  _setActiveUserId(id) {
    this.writeData('activeUserId', id);
  }

  _fetchSchools(): QueryBuilder {
    const activeSchoolId = this.getActiveSchoolId();
    let query = resource('school_v4');

    if (activeSchoolId) {
      query = query.mandarkEndpoint(['schools_v4', this.getActiveSchoolId()]);
    } else {
      query = query.mandarkEndpoint(['schools_v4']).filter({
        school_personnel_v1: {
          teacher_v1: {
            id: this.getActiveUserId()
          },
          role_v1: {
            name: {
              not: 'teacher'
            }
          },
          status: 'approved'
        }
      });
    }

    return query.sort('name');
  }

  _fetchSchoolsWithUserCounts(): QueryBuilder {
    return this._fetchSchools()
      .withMeta('school_v4')
      .page(this.getPaginationStore().activePage)
      .pageSize(this.getPageSize());
  }

  getActiveUserId(): string {
    return this.readData('activeUserId');
  }

  getAllSchools(): List<SchoolModelV4> | SchoolModelV4 {
    return this._fetchSchools()
      .pageSize(constants.MANDARK_MAX_PAGE_SIZE)
      .getResource();
  }

  getPageOfSchools(): List<SchoolModelV4> | SchoolModelV4 {
    return this._fetchSchoolsWithUserCounts().getResource();
  }

  getSchoolsTotalPages(): number {
    const count = this._fetchSchoolsWithUserCounts().getResourceMetadata();
    return count.getIn(['page', 'total_pages'], 1);
  }

  getActiveSchoolId(): string {
    return this.readData('activeSchoolId');
  }

  getActiveSchool(): SchoolModelV4 {
    return List.isList(this.getAllSchools())
      ? this.getAllSchools().find((school) => school.getId() === this.getActiveSchoolId())
      : this.getAllSchools();
  }

  getPaginationStore(): Store {
    return this._paginationStore;
  }

  getPageSize(): number {
    return this._pageSize;
  }

  getMenuItems(): List<*> {
    return this.readData('menuItems');
  }

  _initializeMenuItems(schools: List<SchoolModelV4>) {
    this.writeData('menuItems', schools);
  }

  isAdminOfActiveSchool(): boolean {
    return (
      this.getSchools().findIndex((school) => school.getId() === this.getActiveSchoolId()) > -1
    );
  }

  isSchoolDataReady(): boolean {
    return this._fetchSchoolsWithUserCounts().isResourceReady();
  }

  hasActiveSchool(): boolean {
    if (!this.getActiveSchoolId()) {
      return false;
    }
    return this._fetchSchools().isResourceReady();
  }
}

export default new SchoolAdministrationStore(STORE_NAME);
